import { useEffect, useState } from 'react';
import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import type { NostrEvent, NostrMetadata } from '@nostrify/nostrify';
import {
  computeScore,
  FOLLOWER_SAMPLE_LIMIT,
  INFLUENCE_SAMPLE_LIMIT,
  identityChecks,
  median,
  WOT_INDEXER_RELAYS,
  WOT_PROVIDERS,
  type WotMetrics,
  type WotResult,
} from '@/lib/wot';

export type WotPhase =
  | 'idle'
  | 'profile'
  | 'assertion'
  | 'graph'
  | 'influence'
  | 'score'
  | 'done';

export const WOT_PHASE_STEPS: { key: WotPhase; label: string }[] = [
  { key: 'profile', label: 'Fetching profile & contact list' },
  { key: 'assertion', label: 'Reading trusted assertions (NIP-85)' },
  { key: 'graph', label: 'Mapping follower graph' },
  { key: 'influence', label: 'Sampling follower influence' },
  { key: 'score', label: 'Computing trust score' },
];

function intTag(tags: string[][], name: string): number | null {
  const raw = tags.find(([n]) => n === name)?.[1];
  if (!raw) return null;
  const n = parseInt(raw, 10);
  return Number.isFinite(n) ? n : null;
}

function withTimeout(signal: AbortSignal, ms: number): AbortSignal {
  return AbortSignal.any([signal, AbortSignal.timeout(ms)]);
}

/** Query every NIP-85 provider relay for kind 30382 assertions about `subjects`. */
async function queryProviders(
  nostr: ReturnType<typeof useNostr>['nostr'],
  subjects: string[],
  signal: AbortSignal,
): Promise<NostrEvent[]> {
  const results = await Promise.all(
    WOT_PROVIDERS.map((p) =>
      nostr
        .relay(p.relay)
        .query(
          [
            {
              kinds: [30382],
              authors: [p.pubkey],
              '#d': subjects,
              limit: subjects.length,
            },
          ],
          { signal: withTimeout(signal, 8_000) },
        )
        .catch(() => [] as NostrEvent[]),
    ),
  );
  return results.flat();
}

/** First non-null integer tag across provider assertions, in priority order. */
function pickTag(
  assertionsByProvider: NostrEvent[][],
  name: string,
): number | null {
  for (const events of assertionsByProvider) {
    for (const event of events) {
      const value = intTag(event.tags, name);
      if (value !== null) return value;
    }
  }
  return null;
}

/**
 * Fetches everything needed to score a pubkey's web of trust and returns the
 * computed report. Phases are exposed so the UI can show live progress.
 */
export function useWotScore(pubkey: string | undefined) {
  const { nostr } = useNostr();
  const [phase, setPhase] = useState<WotPhase>('idle');

  // Restart the progress display whenever a new subject is analyzed.
  useEffect(() => {
    setPhase('profile');
  }, [pubkey]);

  const query = useQuery<WotResult>({
    queryKey: ['wot-score', pubkey],
    enabled: !!pubkey,
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
    retry: false,
    queryFn: async ({ signal }) => {
      if (!pubkey) throw new Error('No pubkey');

      const indexers = nostr.group([...WOT_INDEXER_RELAYS]);

      // ── Phase 1: profile metadata + own contact list ──────────────────
      setPhase('profile');
      const [profileEvents, contactEvents] = await Promise.all([
        indexers
          .query([{ kinds: [0], authors: [pubkey], limit: 1 }], {
            signal: withTimeout(signal, 10_000),
          })
          .catch(() => []),
        indexers
          .query([{ kinds: [3], authors: [pubkey], limit: 1 }], {
            signal: withTimeout(signal, 10_000),
          })
          .catch(() => []),
      ]);

      let metadata: NostrMetadata | undefined;
      try {
        if (profileEvents[0]) {
          metadata = JSON.parse(profileEvents[0].content) as NostrMetadata;
        }
      } catch {
        metadata = undefined;
      }

      const following = new Set<string>(
        (contactEvents[0]?.tags ?? [])
          .filter(([name, value]) => name === 'p' && value)
          .map(([, value]) => value),
      );

      // ── Phase 2: NIP-85 trusted assertions for the subject ────────────
      setPhase('assertion');
      const subjectAssertions = await Promise.all(
        WOT_PROVIDERS.map((p) =>
          nostr
            .relay(p.relay)
            .query(
              [
                {
                  kinds: [30382],
                  authors: [p.pubkey],
                  '#d': [pubkey],
                  limit: 1,
                },
              ],
              { signal: withTimeout(signal, 8_000) },
            )
            .catch(() => [] as NostrEvent[]),
        ),
      );

      const hasAssertion = subjectAssertions.some((events) => events.length > 0);
      let followers = pickTag(subjectAssertions, 'followers');
      const providerRank = pickTag(subjectAssertions, 'rank');
      const firstCreatedAt = pickTag(subjectAssertions, 'first_created_at');
      const postCount = pickTag(subjectAssertions, 'post_cnt') ?? 0;
      let replies = pickTag(subjectAssertions, 'reply_cnt');
      let reactions = pickTag(subjectAssertions, 'reactions_cnt');
      let zapCount = pickTag(subjectAssertions, 'zap_cnt_recd');
      let zapAmount = pickTag(subjectAssertions, 'zap_amt_recd') ?? 0;
      const reportsReceived = pickTag(subjectAssertions, 'reports_cnt_recd') ?? 0;

      // ── Phase 3: sample the follower graph ────────────────────────────
      setPhase('graph');
      const followerEvents = await indexers
        .query([{ kinds: [3], '#p': [pubkey], limit: FOLLOWER_SAMPLE_LIMIT }], {
          signal: withTimeout(signal, 15_000),
        })
        .catch(() => []);

      const sampledFollowers = [...new Set(followerEvents.map((e) => e.pubkey))];
      const mutualSampleCount = sampledFollowers.filter((pk) =>
        following.has(pk),
      ).length;
      const mutualRatio =
        sampledFollowers.length > 0
          ? mutualSampleCount / sampledFollowers.length
          : 0;

      let estimated = false;
      if (followers === null) {
        // No trusted assertion: fall back to the sample size as a lower bound.
        estimated = true;
        followers = sampledFollowers.length;
      }

      // ── Phase 4: second-degree influence + engagement fallback ────────
      setPhase('influence');
      const influenceSample = sampledFollowers.slice(0, INFLUENCE_SAMPLE_LIMIT);
      let followerInfluence: number | null = null;
      let notableFollowers: { pubkey: string; followers: number }[] = [];

      if (influenceSample.length > 0) {
        const batch = await queryProviders(nostr, influenceSample, signal);

        // Merge: subject → followers, first provider to report wins.
        const bySubject = new Map<string, number>();
        for (const event of batch) {
          const subject = event.tags.find(([n]) => n === 'd')?.[1];
          const count = intTag(event.tags, 'followers');
          if (subject && count !== null && !bySubject.has(subject)) {
            bySubject.set(subject, count);
          }
        }

        const entries = [...bySubject.entries()].map(([pk, count]) => ({
          pubkey: pk,
          followers: count,
        }));
        followerInfluence = median(entries.map((x) => x.followers));
        notableFollowers = entries
          .sort((a, b) => b.followers - a.followers)
          .slice(0, 8);
      }

      let engagementSampled = false;
      if (replies === null && reactions === null && zapCount === null) {
        // Providers published no engagement tags — sample recent activity.
        engagementSampled = true;
        const since = Math.floor(Date.now() / 1000) - 180 * 24 * 60 * 60;
        const engagement = await indexers
          .query(
            [
              {
                kinds: [1, 6, 7, 9735],
                '#p': [pubkey],
                since,
                limit: 400,
              },
            ],
            { signal: withTimeout(signal, 12_000) },
          )
          .catch(() => []);

        replies = engagement.filter((e) => e.kind === 1).length;
        reactions =
          engagement.filter((e) => e.kind === 7).length +
          engagement.filter((e) => e.kind === 6).length;
        zapCount = engagement.filter((e) => e.kind === 9735).length;
        zapAmount = 0;
      }

      // ── Phase 5: compute ──────────────────────────────────────────────
      setPhase('score');

      const metrics: WotMetrics = {
        followers,
        following: following.size,
        sampledFollowers: sampledFollowers.length,
        mutualSampleCount,
        mutualRatio,
        estimatedMutuals:
          followers !== null && sampledFollowers.length > 0
            ? Math.round(mutualRatio * followers)
            : null,
        followerInfluence,
        replies: replies ?? 0,
        reactions: reactions ?? 0,
        zapCount: zapCount ?? 0,
        zapAmount,
        reportsReceived,
        postCount,
        providerRank,
        accountAgeDays:
          firstCreatedAt !== null
            ? Math.max(0, Math.floor((Date.now() / 1000 - firstCreatedAt) / 86400))
            : null,
        identity: identityChecks(metadata),
        lowConfidence: sampledFollowers.length < 10,
        hasAssertion,
        engagementSampled,
        estimated,
      };

      const { components, total, grade } = computeScore(metrics);

      return {
        pubkey,
        metadata,
        metrics,
        components,
        total,
        grade,
        notableFollowers,
      } satisfies WotResult;
    },
  });

  return {
    ...query,
    phase: query.isPending ? phase : query.isError ? 'idle' : 'done',
  };
}
